/* eslint-disable @typescript-eslint/no-explicit-any */
import {ChangeDetectorRef, Component, DoCheck, EventEmitter, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {ModalComponent} from '../modal-service.service';

import {PlanModelService} from '../../../service/plan-model.service';
import {TenureService} from '../../../../plan-information/tenure/tenure.service';
import {FormerTenureValidity} from '../../../models/former-tenure-validity.model';
import {FormerTenureUiConfig} from '../../../models/former-tenure-ui-config';
import {FormerTenureDetailComponent} from '../../../../plan-information/tenure/former-tenure-detail/former-tenure-detail.component';
import {ParentPlanType} from '../../../models/parent-plan-type.model';
import {SubjectLandType} from '../../../models/subject-land-type.model';
import {equals} from '../../../service/data-util';
import {FormsModule, NgForm, NgModel} from '@angular/forms';
import {NlrPermissionsDisabledPipe} from '../../../pipe/nlr-permissions-disabled-pipe';
import {Tenure} from '../../../models/tenure-model';
import {NgEventBus} from 'ng-event-bus';

@Component({
    selector: 'nlr-tenure-modal',
    imports: [FormerTenureDetailComponent, FormsModule, NlrPermissionsDisabledPipe],
    templateUrl: './tenure-modal.component.html',
    styleUrls: ['../modal.scss', './tenure-modal.component.scss']
})
export class TenureModalComponent implements ModalComponent, OnInit, DoCheck {
    @ViewChild('tenureModalForm')
    tenureModalForm: NgForm;

    modalClosed: EventEmitter<any> = new EventEmitter();

    modalSize: string = 'modal-xlg';

    confirmationText: string;

    action: string = 'Close dialog';

    formerTenureUIConfigLookup: FormerTenureUiConfig;

    get readonly() {
        return this.planModelService.readonly;
    }

    tenureDisplayType: string;
    formerTenureValidityLookup: FormerTenureValidity;
    parentPlanTypeLookups: ParentPlanType[];
    subjectLandTypeLookups: SubjectLandType[];

    changedTenures = [];

    model: any;

    private verifiedModel = {
        formerTenures: undefined
    };

    private modelAtLastCheck: any;

    private pristineModel: any;

    constructor(
        private planModelService: PlanModelService,
        private nlrpTenureService: TenureService,
        private cdr: ChangeDetectorRef,
        private eventBus: NgEventBus
    ) {}

    ngOnInit(): void {
        this.model = this.nlrpTenureService.toDetailViewModel(this.planModelService.getModel());

        if (this.model.tenures.length === 0) {
            this.addTenure();
        }

        this.pristineModel = structuredClone(this.model);
    }

    ngDoCheck() {
        if (!equals(this.model, this.modelAtLastCheck)) {
            this.modelAtLastCheck = structuredClone(this.model);

            this.checkChangedTenure();

            if (this.verifiedModel.formerTenures) {
                delete this.verifiedModel.formerTenures;
            }

            this.nlrpTenureService.validate(this.model);
        }
    }

    applyAndClose() {
        this.modalClosed.emit(this.action);
    }
    closeDialog() {
        this.modalClosed.emit('Close dialog');
    }

    trackByIndex(index: number, tenure: Tenure): any {
        return index;
    }

    addTenure(idx?: number) {
        const newTenure = {
            lots: [],
            parentPlanType: undefined,
            parentPlan: ''
        };

        if (this.formerTenureValidityLookup.defaultPlanType) {
            newTenure.parentPlanType = this.formerTenureValidityLookup.defaultPlanType;
        }

        if (this.formerTenureValidityLookup.prePopParentPlan) {
            newTenure.parentPlan = this.model.planNumber;
        }
        const index = idx != undefined ? idx + 1 : 0;
        this.model.tenures = [...this.model.tenures.slice(0, index), newTenure, ...this.model.tenures.slice(index)];
        this.cdr.detectChanges();
        this.eventBus.cast('add-new-tenure', index);
    }

    removeTenure(idx: number) {
        this.model.tenures.splice(idx, 1);
        if (this.model.tenures.length === 0) {
            this.addTenure();
        }
    }

    hasChangedBurdenedAffectedTenures() {
        const planModel = this.planModelService.getModel();

        return (
            planModel.planPurpose.parentTenureAssociationType === 'AFFECTED_TENURE' &&
            this.changedTenures.some(t =>
                planModel.interests.some((i: any) => i.burdenedAffectedTenures.some((bat: any) => bat.affectedTenure.id === t.id))
            )
        );
    }

    checkChangedTenure() {
        this.changedTenures.length = 0;

        if (this.pristineModel && !equals(this.pristineModel, this.model)) {
            const tenureIds = this.model.tenures.map((t: any) => t.id).map((id: any) => id);
            this.changedTenures.push(
                ...this.pristineModel.tenures.filter((tenure: any) => !tenureIds.includes(tenure.id)),
                ...this.model.tenures.filter(
                    (tenure: any) =>
                        tenure.id &&
                        !this.pristineModel.tenures.find(
                            (previousTenure: any) =>
                                previousTenure.id === tenure.id &&
                                previousTenure.parentPlan === tenure.parentPlan &&
                                previousTenure.parentPlanType === tenure.parentPlanType &&
                                previousTenure.lots.length === tenure.lots.length &&
                                previousTenure.lots.every((previousLot: any) =>
                                    tenure.lots.find(
                                        (lot: any) =>
                                            lot.nonLotCommonProperty === previousLot.nonLotCommonProperty &&
                                            (lot.nonLotCommonProperty || lot.parentLotIdentifier === previousLot.parentLotIdentifier)
                                    )
                                )
                        )
                )
            );
        }
    }

    checkInterest(tenure: any) {
        let match = false;
        const currentInterests = this.planModelService.getModel().interests;
        currentInterests.forEach((interest: any) => {
            if (interest.burdenedAffectedTenures.length > 0) {
                interest.burdenedAffectedTenures.forEach((burdenedTenure: any) => {
                    if (burdenedTenure.affectedTenure.id === tenure.id) {
                        match = true;
                    }
                });
            }
        });
        return match;
    }

    isVerifyDisabled() {
        return this.model.tenures.some((tenure: any) => tenure.lots.filter((lot: any) => lot.nonLotCommonProperty).length > 1);
    }

    verify() {
        Object.values(this.tenureModalForm.controls).forEach(control => {
            control.markAsTouched();
        });
        this.tenureModalForm.onSubmit(undefined);
        this.verifiedModel = this.nlrpTenureService.verify(this.model);
    }

    isSaveDisabled() {
        return this.tenureModalForm?.invalid || !this.verifiedModel.formerTenures || this.hasUnverified();
    }

    save() {
        this.pristineModel = structuredClone(this.model);
        this.modalClosed.emit(this.verifiedModel);
    }

    hasUnverified() {
        return this.model.tenures.some(
            (tenure: any) =>
                tenure.lots.filter((lot: any) =>
                    lot.nonLotCommonProperty
                        ? this.nlrpTenureService.getNonLotCommonPropertyResponse(tenure.parentPlanType, tenure.parentPlan)?.error !== undefined
                        : this.nlrpTenureService.getParentLotResponse(tenure.parentPlanType, tenure.parentPlan, lot.parentLotIdentifier)?.error !==
                          undefined
                ).length > 0
        );
    }
}
